import { useAutoneTranslation } from '@autone/translations';
import { format } from 'date-fns';
import { type ComponentProps, forwardRef, useCallback, useMemo } from 'react';

import {
  DatePicker,
  Input,
  NumberInput,
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../../atoms';
import { type SingleDatePickerProps } from '../../../../atoms/DatePicker';
import { type InputProps } from '../../../../atoms/inputs/Input';
import { type NumberInputProps } from '../../../../atoms/inputs/NumberInput';
import { cn } from '../../../../utils';

/**
 * A default text input for string values.
 */
const DefaultStringInput = forwardRef<HTMLInputElement, InputProps>(
  ({ ...props }, ref) => {
    const { common } = useAutoneTranslation();

    return (
      <Input
        type="text"
        variant={'default'}
        className="rounded-l-none border-l-0"
        placeholder={common('advanced-filters.value')}
        {...props}
        ref={ref}
      />
    );
  },
);

DefaultStringInput.displayName = 'AdvancedFilterDefaultStringInput';

/**
 * A default number input for numeric values.
 */
const DefaultNumberInput = forwardRef<HTMLInputElement, NumberInputProps>(
  ({ ...props }, ref) => {
    const { common } = useAutoneTranslation();

    return (
      <NumberInput
        placeholder={common('advanced-filters.value')}
        className="rounded-l-none border-l-0"
        wrapperClassName="rounded-l-none"
        {...props}
        ref={ref}
      />
    );
  },
);

interface DefaultSelectInputProps<T extends string = string>
  extends Omit<ComponentProps<typeof Select>, 'value' | 'onValueChange'> {
  value?: T;
  onValueChange: (value: T) => void;
  options: { id: T; label: string }[];
}

/**
 * A default input for single-select values.
 */
function DefaultSingleSelectInput<T extends string = string>({
  value,
  onValueChange,
  options,
  ...props
}: DefaultSelectInputProps<T>) {
  const { common } = useAutoneTranslation();

  return (
    <Select value={value} onValueChange={onValueChange} {...props}>
      <SelectTrigger
        id="advanced-filters-trigger"
        aria-label="Advanced filters trigger"
        className="rounded-l-none border-l-0"
      >
        <SelectValue placeholder={common('advanced-filters.value')} />
      </SelectTrigger>
      <SelectContent data-testid="advanced-filters-column-select-content">
        <SelectGroup>
          {options.map(({ id, label }) => (
            <SelectItem key={id} value={id}>
              {label}
            </SelectItem>
          ))}
        </SelectGroup>
      </SelectContent>
    </Select>
  );
}

DefaultNumberInput.displayName = 'AdvancedFilterDefaultNumberInput';

interface DefaultSingleDatePickerProps
  extends Omit<SingleDatePickerProps, 'date' | 'onChange' | 'placeholderText'> {
  /**
   * A date string in 'yyyy-MM-dd' format.
   */
  value?: string;
  /**
   * Callback when the date value changes. The value is a date string in 'yyyy-MM-dd' format.
   */
  onValueChange: (value?: string) => void;
  placeholderText?: string;
}

/**
 * A default single date picker input for single date values. Uses date strings in 'yyyy-MM-dd'
 * format.
 */
const DefaultSingleDatePicker = forwardRef<
  HTMLButtonElement,
  DefaultSingleDatePickerProps
>(({ value, onValueChange, inputClassName, ...props }, ref) => {
  const { common } = useAutoneTranslation();

  const valAsDate = useMemo(
    () => (value ? new Date(value) : undefined),
    [value],
  );

  const handleChange = useCallback(
    (date: Date) => {
      onValueChange(format(date, 'yyyy-MM-dd'));
    },
    [onValueChange],
  );

  return (
    <DatePicker
      date={valAsDate}
      onChange={handleChange}
      inputClassName={cn(
        'w-full h-12 rounded-md rounded-l-none border-l-0',
        inputClassName,
      )}
      placeholderText={common('pick-a-date')}
      {...props}
      ref={ref}
    />
  );
});

DefaultSingleDatePicker.displayName = 'AdvancedFilterDefaultSingleDatePicker';

export const AdvancedFiltersDefaultInputs = {
  String: DefaultStringInput,
  Number: DefaultNumberInput,
  SingleDate: DefaultSingleDatePicker,
  SingleSelect: DefaultSingleSelectInput,
};
